// eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useEffect, useState } from "react";
import {
  Grid,
  Container,
  Card,
  Button,
  FormControl,
  FormLabel,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Divider,
  ListItemSecondaryAction,
  Box,
} from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAccessToken, useAuth } from "../../context/auth";
import { GetGenericListDetails } from "../../services/api/accident-service";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Header, { HeaderType } from "../../components/Header";
import type {
  IDocument,
  IQuestionAnswer,
} from "../../services/model/accidentSupportRequest";
import {
  GetCurrentForm,
  NavigateTo,
  SetLocation,
} from "../../helper/navigation";
import { NEW_LIST_RECORD_ID_KEY } from "./helpers/genericForm.helper";

import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { v4 as uuidv4 } from "uuid";

interface ListViewProps {
  style?: any;
  loading: boolean;
}

const LIST_MODE_KEY = "listMode";
const LIST_DRAFT_ID_KEY = "listDraftId";

const clearLocalMediaDraftState = () => {
  localStorage.removeItem("linkedDocumentID");
  localStorage.removeItem("documentCount");
  localStorage.removeItem("recordingId");
  localStorage.removeItem("currentRecording");
  localStorage.removeItem("voiceCount");
  localStorage.removeItem("dynamicViewVehicleId");
  localStorage.removeItem("viewVehicleId");
  localStorage.removeItem("activeCameraPreviewField");

  const toDelete: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith("cameraPreview:")) toDelete.push(k);
  }
  toDelete.forEach((k) => localStorage.removeItem(k));
};

const GenericListForm: React.FC<ListViewProps> = (_props) => {
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const navigate = useNavigate();
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();
  const [answers, setAnswers] = useState<IQuestionAnswer[]>([]);
  const [formTitle, setFormTitle] = useState<string>("");
  const [formDescription, setFormDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const { tenant } = useParams<{ tenant: string }>();
  const [sp] = useSearchParams();
  const node = sp.get("node"); // current formSectionId

  const cameFromFinal =
    sp.get("from") === "final" ||
    sessionStorage.getItem("returnToFinal") === "1";

  const makeDraftId = () => {
    // crypto.randomUUID is not available everywhere (some WebViews)
    try {
      return crypto.randomUUID();
    } catch {
      return uuidv4();
    }
  };

  const goToFinal = () => {
    const url = sessionStorage.getItem("finalReturnUrl");
    if (url) {
      navigate(url, { replace: true });
      return;
    }

    sessionStorage.removeItem(LIST_MODE_KEY);
    sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);

    const base = tenant ? `/${tenant}` : "";
    navigate(`${base}/accidentSupportFinal`, { replace: true });
  };

  // tenant-aware absolute navigation
  const go = (
    path: string,
    opts?: import("react-router-dom").NavigateOptions,
  ) => {
    const clean = path.replace(/^\/+/, "");
    navigate(tenant ? `/${tenant}/${clean}` : `/${clean}`, opts);
  };

  const getStepNode = () => {
    const search = new URLSearchParams(window.location.search);
    return {
      step: search.get("step") ?? "0",
      node: search.get("node") ?? "",
    };
  };

  const cancel = () => {
    // clear list-mode when leaving this list screen via Back
    sessionStorage.removeItem(LIST_MODE_KEY);
    sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);

    const incidentType = JSON.parse(
      localStorage.getItem("incidentType") || "null",
    );

    const params = new URLSearchParams(window.location.search);
    const step = Number(params.get("step") ?? "0");

    if (step <= 0) {
      if (incidentType === "AccidentSupport")
        go("accidentSupport", { replace: true });
      else go("accidentSupportList", { replace: true });
      return;
    }

    const prev = step - 1;
    SetLocation(prev);
    go(NavigateTo({ step: prev }), { replace: true });
  };

  const addRecord = () => {
    const { step, node } = getStepNode();

    // Starting a brand-new list item must not inherit media from prior items.
    clearLocalMediaDraftState();

    localStorage.setItem("returnToList", "1");
    localStorage.setItem("returnStep", step);
    localStorage.setItem("returnNode", node);

    localStorage.removeItem("ListId");
    localStorage.removeItem("viewVehicleId");
    localStorage.removeItem("dynamicViewVehicleId");

    sessionStorage.setItem(LIST_MODE_KEY, "new");
    // Always start "Add New" with a fresh list record id.
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);

    // stable draft id for this new record
    const draftId = makeDraftId();
    sessionStorage.setItem(LIST_DRAFT_ID_KEY, draftId);

    const qs = `?step=${encodeURIComponent(step)}&node=${encodeURIComponent(
      node,
    )}&from=list`;
    navigate(tenant ? `/${tenant}/dynamicform${qs}` : `/dynamicform${qs}`);
  };

  const viewRecordDetail = (listId: string) => {
    localStorage.setItem("ListId", JSON.stringify(listId));
    sessionStorage.removeItem(LIST_MODE_KEY);
    sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);
    navigate(tenant ? `/${tenant}/dynamicfromview` : `/dynamicfromview`);
  };

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title,
      description: message,
    });
  };

  const routeNext = () => {
    const params = new URLSearchParams(window.location.search);
    const step = Number(params.get("step") ?? "0");
    const nextStep = step + 1;

    sessionStorage.removeItem(LIST_MODE_KEY);
    sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
    sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);

    SetLocation(nextStep);
    go(NavigateTo({ step: nextStep }), { replace: true });
  };

  const fetchListRecords = async () => {
    try {
      setLoading(true);

      const accidentId = JSON.parse(
        localStorage.getItem("accidentId") || "null",
      );

      const current = GetCurrentForm();
      const formId =
        current?.id ?? JSON.parse(localStorage.getItem("formTypeId") || "null");

      if (!formId) {
        setAnswers([]);
        setLoading(false);
        return;
      }

      const listData = await GetGenericListDetails(token, accidentId, formId);
      if (!listData) throw new Error("No response");

      const raw: any[] = listData.data ?? [];
      const mapped: IQuestionAnswer[] = raw.map((r) => {
        const rawAns = r["answer"];
        let normalized = "";

        try {
          const parsed =
            typeof rawAns === "string" ? JSON.parse(rawAns) : rawAns;
          if (Array.isArray(parsed)) {
            normalized = parsed.filter(Boolean).join(", ");
          } else if (parsed && typeof parsed === "object") {
            const obj = parsed as Record<string, unknown>;

            // Address answers can carry coords/sidecar keys; show only the human-readable address.
            if (typeof obj.googleAddress === "string" && obj.googleAddress.trim()) {
              normalized = obj.googleAddress.trim();
            } else {
              normalized = Object.entries(obj)
                .filter(([k, v]) => {
                  if (k.endsWith("__latitude") || k.endsWith("__longitude")) return false;
                  if (k === "latitude" || k === "longitude" || k === "lat" || k === "lng") return false;
                  return v != null && v !== "";
                })
                .map(([, v]) => String(v))
                .join(", ");
            }
          } else {
            normalized = String(rawAns ?? "");
          }
        } catch {
          normalized = String(rawAns ?? "");
        }

        return {
          id: r["id"] as string,
          questionId: r["questionId"] as string,
          question: r["question"] as string,
          answer: normalized,
          listId: r["listId"] as string,
          formTitle: r["formTitle"] as string,
          questionType: r["questionType"] as string,
          images: (r["images"] as [IDocument]) ?? [],
          recording: (r["recording"] as string) ?? "",
        };
      });

      setAnswers(mapped);
    } catch (err) {
      showDialog(
        "Failure",
        "There seems to be a problem with your internet connection.\nPlease check your internet connection.",
      );
      if (err instanceof Error) console.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const step = Number(params.get("step") ?? "0");
    SetLocation(step);

    const current = GetCurrentForm();
    const formId = current?.id ?? null;

    if (formId) localStorage.setItem("formTypeId", JSON.stringify(formId));
    if (current) {
      setFormTitle(current.name);
      setFormDescription(current?.description || "");
      setIsRequired(!!current?.required);
    } else {
      setIsRequired(false);
    }

    fetchListRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  const NoRecord =
    answers.length === 0 ? (
      <div>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <ListAltIcon style={{ fontSize: 70 }} />
          <br />
          <Typography variant="subtitle2">
            There are no items to display,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;please add a new item.
          </Typography>
        </Grid>
      </div>
    ) : null;

  return (
    <div>
      <Header type={HeaderType.AccidentSupportAdd} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12 as any}>
            <Card variant="outlined" style={{ padding: "15px" }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Grid size={12 as any}>
                  <Card variant="outlined" style={{ padding: "15px" }}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    >
                      <Typography variant="subtitle1">
                        <strong>{formTitle || "List Form"}</strong>
                      </Typography>
                      <Grid container justifyContent="center">
                        <Typography variant="body2">
                          {formDescription && (
                            <Box
                              dangerouslySetInnerHTML={{
                                __html: formDescription || "",
                              }}
                              sx={{
                                "& p": { mb: 1 },
                                "& h1, & h2, & h3": { mt: 2, mb: 1 },
                                "& ul, & ol": { pl: 3, mb: 1 },
                                "& a": { color: "secondary.main" },
                              }}
                            />
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>

                  <br />

                  <Typography variant="subtitle1">
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                    />
                    <Divider variant="middle" />
                    <Button
                      onClick={addRecord}
                      value="add"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                    >
                      <strong>Add New</strong>
                    </Button>
                    <br />
                    <br />
                  </Typography>

                  <Grid size={12 as any}>
                    <div>
                      <List>
                        {answers.map((answer) => (
                          <React.Fragment key={answer.id}>
                            <Card>
                              <ListItem style={{ display: "block" }}>
                                <ListItemText
                                  primary={`${answer.question} : ${answer.answer}`}
                                />
                                <ListItemSecondaryAction>
                                  <IconButton
                                    edge="end"
                                    aria-label="open"
                                    onClick={() =>
                                      viewRecordDetail(answer.listId || "")
                                    }
                                  >
                                    <ChevronRightIcon />
                                  </IconButton>
                                </ListItemSecondaryAction>
                              </ListItem>
                            </Card>
                            <br />
                          </React.Fragment>
                        ))}
                        {NoRecord}
                      </List>
                    </div>
                  </Grid>

                  <Grid size={12 as any}>
                    <FormControl variant="outlined" style={{ width: "100%" }}>
                      <FormLabel
                        ref={inputLabel}
                        htmlFor="demo-simple-select-outlined-label"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Button
                onClick={() => {
                  const params = new URLSearchParams(window.location.search);
                  const step = Number(params.get("step") ?? "0");
                  const node = params.get("node") ?? "";
                  localStorage.setItem(`flowStepDone:${step}:${node}`, "1");

                  if (cameFromFinal) {
                    goToFinal();
                    return;
                  }

                  routeNext();
                }}
                variant="contained"
                color="primary"
                fullWidth
                disabled={isRequired && answers.length === 0}
                size="large"
              >
                <strong>{cameFromFinal ? "Done" : "Next"}</strong>
              </Button>

              {isRequired && answers.length === 0 && (
                <Typography sx={{ mt: 1 }} color="error" variant="body2">
                  This step is required. Please add at least one item to continue.
                </Typography>
              )}

              {!cameFromFinal && (
                <Grid size={12 as any}>
                  <br />
                  <Button
                    onClick={cancel}
                    value="cancel"
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    size="large"
                  >
                    <strong>Back</strong>
                  </Button>
                </Grid>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>

      <BrandLoader open={loading} />
    </div>
  );
};

export default GenericListForm;
